import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { InlineEditor } from "@hid-galaxy-ui/galaxy-react";
import sanitizeHtml from "sanitize-html";
import { notesToolBarConfig } from "../NotesSection/index";
import { useAppSelector } from "../../../../../../../../redux";
import { axiosRestInstance } from "../../../../../../../../common/axios";
import "./HistoryNotesTable.scss";

interface HistoryNotesTableProps {
  partnerNotes: string | null;
  hidNotes: string | null;
  isHIDUser: boolean;
  isEditable: boolean;
  submittedBy?: string | null;
  submittedByAdmin?: string | null;
  partnerSubmittedOn?: string | null;
  hidSubmittedOn?: string | null;
  onHidNotesChange?: (notes: string) => void;
}

const HistoryNotesTable: React.FC<HistoryNotesTableProps> = ({
  partnerNotes,
  hidNotes,
  isHIDUser,
  isEditable,
  submittedBy,
  submittedByAdmin,
  partnerSubmittedOn,
  hidSubmittedOn,
  onHidNotesChange,
}) => {
  const { userList, userListHID } = useAppSelector((state) => state.user);
  const [hidNotesValue, setHidNotesValue] = useState(hidNotes || "");
  const [partnerUserName, setPartnerUserName] = useState<string>("");
  const [adminUserName, setAdminUserName] = useState<string>("");

  // Update local state when hidNotes prop changes
  React.useEffect(() => {
    setHidNotesValue(hidNotes || "");
  }, [hidNotes]);

  const handleHidNotesChange = (value: string) => {
    setHidNotesValue(value);
    if (onHidNotesChange) {
      onHidNotesChange(value);
    }
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString();
    } catch {
      return dateString ?? "-";
    }
  };

  const decodeHtmlEntities = (value: string): string => {
    if (typeof document === "undefined") return value;
    let current = value;
    // Some records arrive double-encoded (&amp;lt;p&amp;gt;...), decode until stable.
    for (let i = 0; i < 3; i += 1) {
      const ta = document.createElement("textarea");
      ta.innerHTML = current;
      const decoded = ta.value;
      if (decoded === current) break;
      current = decoded;
    }
    return current;
  };

  /**
   * Activity log renders sanitized rich text so formatting in the editor
   * (lists, paragraphs, headings, links) is preserved.
   * Also supports legacy plain-text notes by preserving line breaks.
   */
  const toActivityLogSanitizedHtml = (notes: string): string => {
    if (!notes) return "";
    const hasHtmlEntities = /&(lt|gt|amp|quot|nbsp|#\d+|#x[0-9a-f]+);/i.test(notes);
    const decoded = hasHtmlEntities ? decodeHtmlEntities(notes) : notes;
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(decoded);

    if (!hasHtmlTags) {
      // Keep legacy plain-text formatting (newlines) visible in activity log.
      const safeText = sanitizeHtml(decoded, { allowedTags: [], allowedAttributes: {} });
      return safeText.replace(/\r\n|\r|\n/g, "<br />").trim();
    }

    return sanitizeHtml(decoded, {
      allowedTags: [
        "p",
        "br",
        "ul",
        "ol",
        "li",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "a",
      ],
      allowedAttributes: {
        a: ["href", "target", "rel"],
      },
      allowedSchemes: ["http", "https", "mailto", "tel"],
      transformTags: {
        a: sanitizeHtml.simpleTransform("a", {
          target: "_blank",
          rel: "noopener noreferrer",
        }),
      },
    }).trim();
  };

  const hasVisibleNotesContent = (notes: string): boolean =>
    sanitizeHtml(toActivityLogSanitizedHtml(notes), {
      allowedTags: [],
      allowedAttributes: {},
    })
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim().length > 0;

  // Helper function to resolve user name from ID
  const resolveSubmittedByName = (id?: string | null, isAdmin: boolean = false): string => {
    if (!id) return "-";
    const userId = parseInt(id, 10);
    if (isNaN(userId)) return id;
    
    // For admin users, check userListHID
    if (isAdmin) {
      const found = userListHID?.find((u) => u.ID === userId);
      return found ? `${found.first_name} ${found.last_name}` : id;
    }
    
    // For partner users, check userList
    const found = userList?.find((u) => u.ID === userId);
    return found ? `${found.first_name} ${found.last_name}` : id;
  };

  // Reset user names only when the IDs themselves change
  useEffect(() => {
    setPartnerUserName("");
    setAdminUserName("");
  }, [submittedBy, submittedByAdmin]);

  // Fetch user details when IDs are available but not found in lists
  useEffect(() => {
    // Ignore responses from a previous run so a stale result can't overwrite a newer one
    let cancelled = false;
    const setPartnerName = (name: string) => {
      if (!cancelled) setPartnerUserName(name);
    };
    const setAdminName = (name: string) => {
      if (!cancelled) setAdminUserName(name);
    };

    const fetchUserDetails = async () => {
      // Fetch partner user details
      if (submittedBy) {
        const partnerUserId = parseInt(submittedBy, 10);
        if (!isNaN(partnerUserId)) {
          // Check if user is in userList
          const foundInList = userList?.find((u) => u.ID === partnerUserId);
          if (foundInList) {
            setPartnerName(`${foundInList.first_name} ${foundInList.last_name}`);
          } else {
            // Fetch user details from API if not found in list
            try {
              const response = await axiosRestInstance.get(`/user/getUser?ID=${partnerUserId}`);
              if (response.data?.data) {
                const user = response.data.data;
                setPartnerName(`${user.first_name} ${user.last_name}`);
              } else {
                setPartnerName(submittedBy);
              }
            } catch (error) {
              console.error("Error fetching partner user details:", error);
              setPartnerName(submittedBy);
            }
          }
        } else {
          setPartnerName(submittedBy);
        }
      } else {
        setPartnerName("-");
      }

      // Fetch admin user details
      if (submittedByAdmin) {
        const adminUserId = parseInt(submittedByAdmin, 10);
        if (!isNaN(adminUserId)) {
          // Check if user is in userListHID
          const foundInList = userListHID?.find((u) => u.ID === adminUserId);
          if (foundInList) {
            setAdminName(`${foundInList.first_name} ${foundInList.last_name}`);
          } else {
            // Fetch user details from API if not found in list
            try {
              const response = await axiosRestInstance.get(`/user/getUser?ID=${adminUserId}`);
              if (response.data?.data) {
                const user = response.data.data;
                setAdminName(`${user.first_name} ${user.last_name}`);
              } else {
                setAdminName(submittedByAdmin);
              }
            } catch (error) {
              console.error("Error fetching admin user details:", error);
              setAdminName(submittedByAdmin);
            }
          }
        } else {
          setAdminName(submittedByAdmin);
        }
      } else {
        setAdminName("-");
      }
    };

    fetchUserDetails();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedBy, submittedByAdmin, userList.length, userListHID.length]);

  // Build table rows based on user role and editability
  const tableRows: Array<{
    type: string;
    notes: string;
    submittedBy: string;
    submittedOn?: string | null;
  }> = [];
  
  if (isHIDUser && isEditable) {
    // For HID users when editable: show partner notes in table
    tableRows.push({ 
      type: "Partner Notes", 
      notes: partnerNotes || "",
      submittedBy: partnerUserName || resolveSubmittedByName(submittedBy, false) || "-",
      submittedOn: partnerSubmittedOn ?? null,
    });
  } else {
    // For partner users OR HID users when not editable: show both partner and HID notes in table
    tableRows.push({ 
      type: "Partner Notes", 
      notes: partnerNotes || "",
      submittedBy: partnerUserName || resolveSubmittedByName(submittedBy, false) || "-",
      submittedOn: partnerSubmittedOn ?? null,
    });
    tableRows.push({ 
      type: "HID Notes", 
      notes: hidNotes || "",
      submittedBy: adminUserName || resolveSubmittedByName(submittedByAdmin, true) || "-",
      submittedOn: hidSubmittedOn ?? null,
    });
  }

  return (
    <div className="history-notes-section">
      <h3 className="notes-title">Activity Log</h3>

      <div className="notes-table-area">
        <TableContainer component={Paper} className="notes-table">
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell className="type-column">Type</TableCell>
                <TableCell className="notes-column">Notes</TableCell>
                <TableCell className="submitted-on-column">Submitted on</TableCell>
                <TableCell className="submitted-by-column">Submitted by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="type-column">{row.type}</TableCell>
                  <TableCell className="notes-column">
                    {row.notes && hasVisibleNotesContent(row.notes) ? (
                      <div
                        className="notes-content notes-content-rich"
                        dangerouslySetInnerHTML={{
                          __html: toActivityLogSanitizedHtml(row.notes),
                        }}
                      />
                    ) : (
                      <span style={{ color: "#999", fontStyle: "italic" }}>No notes</span>
                    )}
                  </TableCell>
                  <TableCell className="submitted-on-column">
                    {formatDate(row.submittedOn)}
                  </TableCell>
                  <TableCell className="submitted-by-column">
                    {row.submittedBy || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* HID Notes input - only for HID users when editable */}
      {isHIDUser && isEditable && (
        <div className="add-note-section hid-notes-editor">
          <label className="add-note-label" htmlFor="hid-notes-inline-editor">
            HID Notes
          </label>
          <InlineEditor
            id="hid-notes-inline-editor"
            placeholder="Add HID notes here..."
            toolBarConfig={notesToolBarConfig}
            editorData={hidNotesValue}
            handleOnChange={handleHidNotesChange}
          />
        </div>
      )}
    </div>
  );
};

export default HistoryNotesTable;